import { useEffect, useState, useCallback } from "react";
import { Layout, Menu, Avatar, Dropdown } from "antd";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  MailOutlined,
  BellOutlined,
  SettingOutlined,
  FileDoneOutlined,
  ClockCircleOutlined,
  StopOutlined,
  FileAddOutlined,
  PlayCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  TeamOutlined,
  FileTextOutlined,
  DollarCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import "../css/common.css";
import { post } from "../../services/services";
import { ApiURL } from "../../services/apiConstants";
import NotificationModal from "../../components/NotificationModal";
import ForgateCodeModal from "../../components/ForgateCodeModal";

const { Header, Sider, Content } = Layout;
const { SubMenu } = Menu;

const AdminDashboardLayout = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [userData, setUserData] = useState({});
  const [selectedKey, setSelectedKey] = useState("1");
  const [openKeys, setOpenKeys] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isCodeModalOpen, setCodeModalOpen] = useState(false);

  const rootSubmenuKeys = ["sub1", "sub2"];

  // Initialize user data and selected menu from localStorage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user_data"));
    setUserData(storedUser?.data || {});
    const storedKey = localStorage.getItem("selectedKey") || "1";
    setSelectedKey(storedKey);
  }, []);

  // Update selected menu key based on route change
  useEffect(() => {
    switch (location.pathname) {
      case "/":
        setSelectedKey("1");
        setOpenKeys([]);
        break;
      case "/free-registration-list":
        setSelectedKey("2");
        setOpenKeys(["sub1"]);
        break;
      case "/demo-given-list":
        setSelectedKey("3");
        setOpenKeys(["sub1"]);
        break;
      default:
        break;
    }
  }, [location.pathname]);

  // Handle menu click
  const handleMenuClick = ({ key }) => {
    setSelectedKey(key);
    localStorage.setItem("selectedKey", key);
  };

  // Handle submenu open/close
  const handleOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));
    setOpenKeys(
      rootSubmenuKeys.includes(latestOpenKey) ? [latestOpenKey] : keys
    );
  };

  const toggleCollapse = () => setCollapsed((prev) => !prev);

  const logout = useCallback(() => {
    try {
      // Preserve security lock keys
      const preserved = Object.entries(localStorage).filter(([key]) =>
        key.startsWith("security_code_lock_time_")
      );
      localStorage.clear();
      preserved.forEach(([key, value]) => localStorage.setItem(key, value));

      post(ApiURL.logout, new FormData()).finally(
        () => (window.location.href = "/")
      );
    } catch (err) {
      console.error("Logout error:", err);
      localStorage.clear();
      window.location.href = "/";
    }
  }, []);

  const userMenu = (
    <Menu>
      <Menu.Item onClick={logout} key="logout" icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Layout style={{ minHeight: "100vh" }}>
        {/* Sidebar */}
        <Sider
          width={230}
          collapsible
          collapsed={collapsed}
          onCollapse={toggleCollapse}
          className="custom-sider"
        >
          <div className="logo">
            <h2>ADMIN PANEL</h2>
          </div>

          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            onClick={handleMenuClick}
          >
            <Menu.Item key="1" icon={<HomeOutlined />}>
              <Link to="/">Dashboard</Link>
            </Menu.Item>

            <SubMenu key="sub1" icon={<SettingOutlined />} title="Company">
              <Menu.Item key="2" icon={<FileAddOutlined />}>
                <Link to="/free-registration-list">Free Registration</Link>
              </Menu.Item>

              <Menu.Item key="3" icon={<PlayCircleOutlined />}>
                <Link to="/demo-given-list">Demo Given</Link>
              </Menu.Item>

              <Menu.Item key="4" icon={<ClockCircleOutlined />}>
                <Link to="/free-trial-list">Free Trial</Link>
              </Menu.Item>

              <Menu.Item key="5" icon={<ExclamationCircleOutlined />}>
                <Link to="/free-trial-expired-list">Free Trial Expired</Link>
              </Menu.Item>

              <Menu.Item key="6" icon={<CheckCircleOutlined />}>
                <Link to="/paid-subscription-list">Paid Subscription</Link>
              </Menu.Item>

              <Menu.Item key="7" icon={<StopOutlined />}>
                <Link to="/paid-subscription-expired-list">
                  Paid Sub. Expired
                </Link>
              </Menu.Item>
            </SubMenu>

            <Menu.Item key="8" icon={<SafetyOutlined />}>
              <Link to="/role-management">Role Management</Link>
            </Menu.Item>

            <Menu.Item key="9" icon={<TeamOutlined />}>
              <Link to="/hr-management">HR Management</Link>
            </Menu.Item>

            <Menu.Item key="10" icon={<DollarCircleOutlined />}>
              <Link to="/account & Finance management">
                Account & Finance Management Management
              </Link>
            </Menu.Item>

            <Menu.Item key="11" icon={<FileTextOutlined />}>
              <Link to="/Invoice-management">Invoice Management</Link>
            </Menu.Item>

            <SubMenu
              key="sub2"
              icon={<SettingOutlined />}
              title="Bugs & Report"
            >
              <Menu.Item key="13" icon={<FileAddOutlined />}>
                <Link to="/all-issues">All Issues</Link>
              </Menu.Item>
              <Menu.Item key="14" icon={<FileAddOutlined />}>
                <Link to="/bugs">Bugs</Link>
              </Menu.Item>
              <Menu.Item key="15" icon={<FileAddOutlined />}>
                <Link to="/change-requests">Change Requests</Link>
              </Menu.Item>
              <Menu.Item key="16" icon={<FileAddOutlined />}>
                <Link to="/feature-requests">Feature Requests</Link>
              </Menu.Item>
              <Menu.Item key="17" icon={<FileAddOutlined />}>
                <Link to="/feedback">Feedback</Link>
              </Menu.Item>
            </SubMenu>

            <Menu.Item key="18" icon={<QuestionCircleOutlined/>}>
              <Link to="/demo-requests">Demo Requests</Link>
            </Menu.Item>
            <Menu.Item key="19" icon={<PlayCircleOutlined />}>
              <Link to="/general-enquiry">General Enquiry</Link>
            </Menu.Item>

            <Menu.Item key="21" icon={<LogoutOutlined />} onClick={logout}>
              Logout
            </Menu.Item>
          </Menu>
        </Sider>

        {/* Main Layout */}
        <Layout className="site-layout">
          <Header
            className="site-layout-background"
            style={{
              padding: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Email */}
            <div
              style={{
                padding: "0 20px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MailOutlined style={{ marginRight: 8, fontSize: 16 }} />
              <b>Email: </b> {userData?.email}
            </div>

            {/* Header Actions */}
            <div
              style={{
                padding: "0 20px",
                display: "flex",
                alignItems: "center",
                gap: 16,
                color: "#fff",
              }}
            >
              {/* <SecurityScanOutlined style={{ fontSize: 20, cursor: "pointer" }} onClick={() => setCodeModalOpen(true)} /> */}

              <div
                style={{ position: "relative", cursor: "pointer" }}
                onClick={() => setIsNotificationModalOpen(true)}
              >
                <BellOutlined style={{ fontSize: 20 }} />
                {unreadCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      // top: -4,
                      // top: -1,
                      top:5,
                      right: -4,
                      background: "red",
                      color: "#fff",
                      borderRadius: "50%",
                      fontSize: 10,
                      minWidth: 16,
                      height: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {unreadCount}
                  </span>
                )}
              </div>

              <Dropdown overlay={userMenu} placement="bottomRight">
                <div
                  style={{
                    textTransform: "capitalize",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <b>Welcome:</b> {userData?.name}{" "}
                  <Avatar icon={<UserOutlined />} />
                </div>
              </Dropdown>
            </div>
          </Header>

          <Content style={{ margin: "0 16px" }}>
            <div style={{ padding: 15, minHeight: 360 }}>
              <Outlet />
            </div>
          </Content>
        </Layout>
      </Layout>

      {/* Modals */}
      <NotificationModal
        isModalOpen={isNotificationModalOpen}
        setIsModalOpen={setIsNotificationModalOpen}
        handleUnreadCountChange={setUnreadCount}
      />
      <ForgateCodeModal
        isCodeModalOpen={isCodeModalOpen}
        setCodeIsModalOpen={setCodeModalOpen}
      />
    </>
  );
};

export default AdminDashboardLayout;
